/**
 * Подтверждение кошелька подписью ton_proof.
 *
 * Вынесено в отдельный хук, потому что нужно в двух местах: в профиле и на
 * экране приглашений, где без подтверждённого кошелька ссылку выдать
 * нельзя. Раньше приглашения умели только отправить человека в профиль — и
 * кнопка с надписью «подтвердить» ничего не подтверждала.
 *
 * Порядок шагов важен и не случаен:
 *
 *   сервер выдаёт одноразовую строку — без неё подпись можно было бы
 *   принести повторно;
 *
 *   кошелёк отключается и подключается заново — ton_proof он отдаёт только
 *   при новом подключении, у уже подключённого его не запросить;
 *
 *   ждём само подключение, а не открытие окна: openModal резолвится в
 *   момент показа, и кошелька в этот момент ещё нет.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { useTranslation } from 'react-i18next';
import { fetchProofPayload, subscribe } from './api.ts';
import type { Wallet } from '@tonconnect/ui-react';
import { ensureWriteAccess, getInitData } from './telegram.ts';
import { useErrorText } from './errors.ts';
import { reportFailure } from './report.ts';

export type ConfirmState = 'idle' | 'preparing' | 'signing' | 'done' | 'error';

/** Сколько ждать подписи кошелька. Дольше человек всё равно не подтвердит. */
const PROOF_WAIT_MS = 180_000;

/** Одноразовая строка живёт 15 минут; обновляем вдвое чаще — с запасом на
 *  то, сколько человек держит открытым окно кошелька. */
const PAYLOAD_REFRESH_MS = 5 * 60 * 1000;

/**
 * Подпись просится в тот же момент, что и подключение.
 *
 * Кошелёк отдаёт ton_proof только при подключении: у уже подключённого его
 * не запросить — оттого подтверждение и было отдельным обрядом «отключить,
 * подключить заново, подписать». Человек, не прошедший обряд, оставался без
 * писем о собственной сделке и не понимал почему.
 *
 * Здесь строка кладётся в параметры подключения заранее и обновляется по
 * часам — просроченную сервер не примет. Подключился с подписью — тут же
 * отправляем её, и подтверждать отдельно уже нечего.
 *
 * Обряд в профиле остаётся: кошельки, подключённые до этого изменения,
 * подписи не давали, а переподключить их можно только руками.
 */
export function useProofOnConnect(lang: 'ru' | 'en'): void {
  const [tonConnectUI] = useTonConnectUI();
  const qc = useQueryClient();

  useEffect(() => {
    let alive = true;
    // Пока строки нет — окно подключения ждёт её, а не открывается без
    // подписи: второй попытки попросить не будет.
    tonConnectUI.setConnectRequestParameters({ state: 'loading' });

    const refresh = async () => {
      try {
        const { payload } = await fetchProofPayload();
        if (alive) tonConnectUI.setConnectRequestParameters({ state: 'ready', value: { tonProof: payload } });
      } catch {
        // Сервер недоступен — подключаем без подписи, как раньше:
        // подключение важнее подтверждения, а подтвердить можно и потом.
        if (alive) tonConnectUI.setConnectRequestParameters(null);
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), PAYLOAD_REFRESH_MS);

    const stop = tonConnectUI.onStatusChange((w: Wallet | null) => {
      const items = w?.connectItems?.tonProof;
      if (w === null || items === undefined || !('proof' in items)) return;
      const initData = getInitData();
      if (initData === '') return;
      // Разрешение писать спрашивается здесь же: человек подключил кошелёк,
      // значит собрался пользоваться, — а по ссылке-приглашению он попадает
      // сразу в приложение и мог ни разу не написать боту. Ответ не важен:
      // подписку он не отменяет, а письма начнут доходить, когда разрешит.
      void ensureWriteAccess().finally(() => {
        void subscribe({
          initData,
          address: w.account.address,
          publicKey: w.account.publicKey ?? '',
          walletStateInit: w.account.walletStateInit,
          proof: {
            timestamp: items.proof.timestamp,
            domain: items.proof.domain.value,
            payload: items.proof.payload,
            signature: items.proof.signature,
          },
          lang,
        })
          // Профиль спрашивает настройки уведомлений в тот же миг, что и мы
          // — отправляем подпись, и его ответ «настроек нет» успевает прийти
          // раньше нашей. Без сброса кеша экран так и просил бы подтвердить
          // кошелёк, который сервер уже принял: подтверждено на сервере,
          // «подтвердите» на экране.
          .then(() => qc.invalidateQueries({ queryKey: ['notifyPrefs'] }))
          .catch(() => undefined);
      });
    });

    return () => {
      alive = false;
      clearInterval(timer);
      stop();
    };
  }, [tonConnectUI, lang, qc]);
}

export function useConfirmWallet(lang: 'ru' | 'en') {
  const { t } = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const errorText = useErrorText();
  const [state, setState] = useState<ConfirmState>('idle');
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<boolean> => {
    setError(null);
    try {
      setState('preparing');
      const { payload } = await fetchProofPayload();

      tonConnectUI.setConnectRequestParameters({ state: 'ready', value: { tonProof: payload } });
      setState('signing');
      await tonConnectUI.disconnect().catch(() => undefined);

      const connected = await new Promise<NonNullable<typeof tonConnectUI.wallet>>(
        (resolve, reject) => {
          const done = (fn: () => void) => {
            stopStatus();
            stopModal();
            clearTimeout(timer);
            fn();
          };
          const stopStatus = tonConnectUI.onStatusChange((w) => {
            const items = w?.connectItems?.tonProof;
            if (w !== null && items !== undefined && 'proof' in items) {
              done(() => resolve(w));
            } else if (w !== null) {
              // Кошелёк подключился, но подписи не дал — бывает у старых
              // версий, и молчать об этом нельзя.
              done(() => reject(new Error(t('profile.wallet_did_not_sign'))));
            }
          });
          // Окно закрыли руками — это ответ, а не повод ждать дальше.
          const stopModal = tonConnectUI.onModalStateChange((modal) => {
            if (modal.status === 'closed' && tonConnectUI.wallet === null) {
              done(() => reject(new Error(t('profile.proof_cancelled'))));
            }
          });
          const timer = setTimeout(
            () => done(() => reject(new Error(t('profile.proof_timeout')))),
            PROOF_WAIT_MS,
          );
          void tonConnectUI.openModal();
        },
      );

      const proof = connected.connectItems?.tonProof;
      if (proof === undefined || !('proof' in proof)) {
        throw new Error(t('profile.wallet_did_not_sign'));
      }

      // Тот же вопрос, что и при подключении: без разрешения письма
      // упрутся в отказ, а человек как раз просит ему писать.
      await ensureWriteAccess();

      const res = await subscribe({
        initData: getInitData(),
        address: connected.account.address,
        publicKey: connected.account.publicKey ?? '',
        walletStateInit: connected.account.walletStateInit,
        proof: {
          timestamp: proof.proof.timestamp,
          domain: proof.proof.domain.value,
          payload: proof.proof.payload,
          signature: proof.proof.signature,
        },
        lang,
      });
      if (!res.ok) throw new Error(t('profile.wallet_did_not_sign'));
      setState('done');
      return true;
    } catch (e) {
      // Подтверждение кошелька — это подпись в самом кошельке: что там
      // случилось, сервер не узнает, если не сказать.
      reportFailure('подтверждение кошелька', e);
      setError(errorText(e));
      setState('error');
      return false;
    }
  };

  return { confirm, state, error, busy: state === 'preparing' || state === 'signing' };
}
